export type StorageCheckResult = {
  ok: boolean;
  quota: number;
  usage: number;
  needed: number;
  shortBy?: number;
};

async function fetchHead( url: string ): Promise<Headers | null>
{
  try
  {
    const resp = await fetch( url, { method: 'HEAD' } );
    if ( resp.ok ) return resp.headers;
    return null;
  } catch
  {
    return null;
  }
}

async function fetchRangeForTotal( url: string ): Promise<number | null>
{
  try
  {
    const resp = await fetch( url, { headers: { Range: 'bytes=0-0' } } );
    if ( !resp.ok ) return null;
    const cr = resp.headers.get( 'Content-Range' ) || resp.headers.get( 'content-range' );
    if ( !cr ) return null;
    const match = /\/(\d+)$/.exec( cr );
    if ( !match ) return null;
    const total = Number( match[1] );
    return Number.isFinite( total ) ? total : null;
  } catch
  {
    return null;
  }
}

export async function getContentLength( url: string ): Promise<number | null>
{
  const headers = await fetchHead( url );
  if ( headers )
  {
    const cl = headers.get( 'Content-Length' ) || headers.get( 'content-length' );
    if ( cl )
    {
      const n = Number( cl );
      if ( Number.isFinite( n ) ) return n;
    }
  }
  return await fetchRangeForTotal( url );
}

export async function estimateTotalBytes( urls: string[] ): Promise<{ total: number; unknown: string[]; byUrl: Record<string, number | null> }>
{
  const byUrl: Record<string, number | null> = {};
  const results = await Promise.all( urls.map( async ( u ) => ( { url: u, size: await getContentLength( u ) } ) ) );
  let total = 0;
  const unknown: string[] = [];
  for ( const { url, size } of results )
  {
    byUrl[url] = size ?? null;
    if ( typeof size === 'number' ) total += size;
    else unknown.push( url );
  }
  return { total, unknown, byUrl };
}

export async function estimateStorage(): Promise<{ quota: number; usage: number }>
{
  const nav: any = navigator as any;
  if ( nav && nav.storage && typeof nav.storage.estimate === 'function' )
  {
    try
    {
      const est = await nav.storage.estimate();
      const quota = typeof est.quota === 'number' ? est.quota : 0;
      const usage = typeof est.usage === 'number' ? est.usage : 0;
      return { quota, usage };
    } catch
    {
      return { quota: 0, usage: 0 };
    }
  }
  return { quota: 0, usage: 0 };
}

export async function checkStorageCapacity( needed: number ): Promise<StorageCheckResult>
{
  const { quota, usage } = await estimateStorage();
  if ( quota <= 0 ) return { ok: true, quota, usage, needed };
  const free = quota - usage;
  const ok = free >= needed;
  return ok
    ? { ok: true, quota, usage, needed }
    : { ok: false, quota, usage, needed, shortBy: needed - free };
}

